/**
 * WS-NOTES (Oct 20, FD-09 / FD-10) — how a note's provenance is shown, and who the UI offers edit/delete to.
 *
 * The SERVER decides (edit_workflow_note / delete_workflow_note: author, or Manager/Owner authority via the
 * multi-role resolver). This module only mirrors that rule so the controls are not offered to someone the server
 * would refuse. CORE-AE:48 — a person's note keeps its author visibly; an edit by someone else is shown as theirs.
 */
import type { NoteChainMember, WorkflowNote } from '@/hooks/useWorkflowReport';

const ROLE_LABELS: Record<string, string> = {
  OWNER: 'Owner',
  ORG_ADMIN: 'Admin',
  MANAGER: 'Manager',
  REGION_MANAGER: 'Region Manager',
  DISTRICT_MANAGER: 'District Manager',
  AUDITOR: 'Auditor',
  SERVICE_ADVISOR: 'Service Advisor',
  SERVICE_WRITER: 'Service Advisor',
  TECHNICIAN: 'Technician',
  DETAIL_SPECIALIST: 'Detail Specialist',
  DISPATCHER: 'Dispatcher',
  PARTS: 'Parts',
};

/** "Owner · Technician" — the roles the author held when writing. Empty when the note predates the snapshot. */
export function formatNoteRoles(roles: string[] | null | undefined): string {
  if (!roles || roles.length === 0) return '';
  const seen = new Set<string>();
  const out: string[] = [];
  for (const r of roles) {
    if (r === 'STAFF') continue;
    const label = ROLE_LABELS[r] ?? r.charAt(0) + r.slice(1).toLowerCase().replace(/_/g, ' ');
    if (!seen.has(label)) { seen.add(label); out.push(label); }
  }
  return out.join(' · ');
}

export function formatTicketNumber(member: Pick<NoteChainMember, 'simple_ticket_number' | 'ticket_id'>): string {
  return member.simple_ticket_number != null
    ? `#TKT-${String(member.simple_ticket_number).padStart(4, '0')}`
    : `#${member.ticket_id.slice(0, 8)}`;
}

/**
 * Where a note was written. For a single-ticket repair the origin is implicit (null); across a rollover chain
 * every note says which day and ticket it came from, including "this ticket".
 */
export function noteOriginLabel(
  origin: NoteChainMember | undefined,
  chainLength: number,
  chain?: readonly NoteChainMember[],
): string | null {
  if (!origin || chainLength <= 1) return null;
  const segment = chain && !isPureRolloverChain(chain) ? describeSegment(origin, chain) : `Day ${origin.chain_position}`;
  return `${segment} · ${formatTicketNumber(origin)}${origin.is_current ? ' (this ticket)' : ''}`;
}

/** A plain multiday repair: only its original and its rollover days (the round-1 shape, labelled "Day N"). */
export function isPureRolloverChain(chain: readonly NoteChainMember[]): boolean {
  return chain.every((c) => !c.relation || c.relation === 'original' || c.relation === 'rollover_day');
}

/**
 * Oct 20 addendum N1: which segment of the logical repair a ticket is, from the SERVER's lineage (ticket_note_chain),
 * never from vehicle or customer matching. "Split part 2 of 3 · Day 2" is the second day of the second part.
 */
export function describeSegment(m: NoteChainMember, chain: readonly NoteChainMember[]): string {
  const day = m.day_number ?? 1;
  const withDay = (base: string) => (day > 1 ? `${base} · Day ${day}` : base);
  switch (m.relation) {
    case 'split_original':
      return 'Original (before split)';
    case 'split_part':
      return withDay(`Split part ${m.split_sequence ?? '?'}${m.split_count ? ` of ${m.split_count}` : ''}`);
    case 'merged':
      return withDay('Merged ticket');
    case 'merged_source':
      return 'Merged in';
    case 'return_continuation':
      return withDay('Return continuation');
    case 'rollover_day': {
      const root = chain.find((c) => c.ticket_id === m.run_root && c.ticket_id !== m.ticket_id);
      return `${root ? describeSegment(root, chain) : 'Original'} · Day ${day}`;
    }
    default:
      return withDay('Original');
  }
}

/** The notes header scope: "All 3 days of this repair" for a multiday repair, "All 4 tickets …" once it has split. */
export function noteChainScopeLabel(chain: readonly NoteChainMember[]): string | null {
  if (chain.length <= 1) return null;
  return isPureRolloverChain(chain)
    ? `All ${chain.length} days of this repair`
    : `All ${chain.length} tickets of this repair`;
}

export interface NoteViewer {
  userId: string | null | undefined;
  /**
   * An actual Owner or Manager of the active shop, multi-role aware (usePermissions.hasRole('OWNER','MANAGER')).
   * Oct 20 addendum N4: ORG_ADMIN authority alone is NOT a basis for correcting someone else's note.
   */
  canCorrectOthers: boolean;
}

export function isOwnNote(note: Pick<WorkflowNote, 'created_by'>, viewer: NoteViewer): boolean {
  return !!viewer.userId && note.created_by === viewer.userId;
}

/** Offer Edit / Delete: the author, or a Manager/Owner. Never on a deleted note. */
export function canModifyNote(note: Pick<WorkflowNote, 'created_by' | 'deleted_at'>, viewer: NoteViewer): boolean {
  if (note.deleted_at) return false;
  return isOwnNote(note, viewer) || viewer.canCorrectOthers;
}

/** Offer "History": the server shows revisions to Managers/Owners, the author and an editor (RLS decides). */
export function canViewNoteHistory(note: Pick<WorkflowNote, 'created_by' | 'edited_by' | 'deleted_by' | 'edit_count'>, viewer: NoteViewer): boolean {
  if (!note.edit_count) return false;
  return viewer.canCorrectOthers || isOwnNote(note, viewer)
    || (!!viewer.userId && (note.edited_by === viewer.userId || note.deleted_by === viewer.userId));
}

/** Notes in display order. Default: oldest first, newest at the bottom (FD-09). */
export function orderNotes<T extends { created_at: string; id: string }>(notes: T[], newestFirst = false): T[] {
  const sorted = [...notes].sort((a, b) => {
    const d = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
    return d !== 0 ? d : a.id.localeCompare(b.id);
  });
  return newestFirst ? sorted.reverse() : sorted;
}
